import type { Space } from '@cocoonspace/sdk-js/types/space'
import type { searchConfig } from '@cocoonspace/shared/config/search-config'
import { ArrowRight } from 'lucide-react'
import { Trans, useTranslation } from 'next-i18next'
import Link from 'next/link'
import { useRouter } from 'next/router'
import type { ComponentProps } from 'react'
import { SpaceCarousel } from '~/components/organisms/space-carousel'
import { buttonVariants } from '~/components/ui/button'
import { cn } from '~/lib/utils'
import { useTracking } from '~/providers/tracking-provider'
import styles from '../pages/index.module.css'
import { SpaceSectionTitle } from './space-section-title'

type DistrictZone = keyof typeof searchConfig.districts

export type DistrictConfig =
	(typeof searchConfig.districts)[DistrictZone][number]

export const SpaceSuggestions = ({
	space,
	suggestions,
	district,
	id,
}: {
	id: ComponentProps<'div'>['id']
	space: Space
	suggestions: Space[]
	district?: DistrictConfig
}) => {
	const router = useRouter()
	const { t } = useTranslation()
	const { track } = useTracking()

	const isDesk = router.route === '/desks/[zone]/[slug]'

	return (
		<div
			id={id}
			className={cn(`container flex flex-col gap-4`, styles.navlink)}
		>
			<SpaceSectionTitle>
				{isDesk
					? t('space:sections.suggestions.desk.title.long')
					: t('space:sections.suggestions.cocoon.title.long')}
			</SpaceSectionTitle>

			<Trans
				parent='div'
				i18nKey={
					!!isDesk || !district?.spaceSuggestionBody
						? 'space:sections.suggestions.bodyDefault'
						: undefined
				}
				defaults={district?.spaceSuggestionBody}
				values={{
					zone: space.address.city,
				}}
			/>

			<SpaceCarousel
				spaces={suggestions.slice(0, 6)}
				onCardClick={(suggestion) => {
					track.spaceSuggestionsCardClick({
						source: space.title,
						target: suggestion.title,
					})
				}}
			/>

			<div className='text-left md:text-right'>
				<Link
					data-testid='see-all-suggestions-link'
					href={`/salle-de-reunion/${space.zone}/${district?.uriParam || ''}`}
					target='_blank'
					className={buttonVariants({ variant: 'textPrimary' })}
					onClick={() => track.spaceSuggestionsSeeMore({ space })}
				>
					{t('space:seeAllSuggestions')}

					<ArrowRight
						size={16}
						className='ml-1'
					/>
				</Link>
			</div>
		</div>
	)
}
